import React, { useEffect, useState } from "react";
import { Users, Repeat } from "lucide-react";
import { KpiCard } from "../shared/KpiCard";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { StatusBadge } from "../shared/StatusBadge";
import { ErrorState } from "../shared/ErrorState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { subscriptionStatusMeta } from "../../lib/statusMaps";
import { formatNumber } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

export function SubscriptionsReportTab() {
  const [dateFrom, setDateFrom] = useListState("reports.subsReport.dateFrom", "");
  const [dateTo, setDateTo] = useListState("reports.subsReport.dateTo", "");
  const [planId, setPlanId] = useListState("reports.subsReport.planId", "");
  const [city, setCity] = useListState("reports.subsReport.city", "");
  const [plans, setPlans] = useState([]);
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    apiClient.get("/plans/active").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const load = () => {
    setState({ loading: true, error: null, data: null });
    apiClient.get("/reports/customers-subscriptions", {
      params: { date_from: dateFrom || undefined, date_to: dateTo || undefined, plan_id: planId || undefined, city: city || undefined },
    })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the subscription report."), data: null }));
  };

  useEffect(load, [dateFrom, dateTo, planId, city]);

  const r = state.data;

  return (
    <div className="mt-4 space-y-4" data-testid="subscriptions-report-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="subs-report-date-from">Created From</Label>
          <Input id="subs-report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="subs-report-date-from-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="subs-report-date-to">Created To</Label>
          <Input id="subs-report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="subs-report-date-to-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="subs-report-plan">Plan</Label>
          <Select value={planId || "all"} onValueChange={(v) => setPlanId(v === "all" ? "" : v)}>
            <SelectTrigger id="subs-report-plan" className="w-48" data-testid="subs-report-plan-select"><SelectValue placeholder="All plans" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              {plans.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="subs-report-city">City</Label>
          <Input id="subs-report-city" placeholder="e.g. Edmonton" value={city} onChange={(e) => setCity(e.target.value)} className="w-40" data-testid="subs-report-city-input" />
        </div>
        {(dateFrom || dateTo || planId || city) && (
          <Button variant="secondary" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setPlanId(""); setCity(""); }} data-testid="subs-report-clear-button">Clear</Button>
        )}
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="subs-report-error" />
      ) : state.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <KpiCard testId="subs-report-kpi-customers" title="Customers" icon={Users} value={formatNumber(r.total_customers)} />
            <KpiCard testId="subs-report-kpi-subscriptions" title="Subscriptions (matching filters)" icon={Repeat} accent value={formatNumber(r.total_subscriptions)} />
          </div>

          <Card data-testid="subs-report-status-card">
            <CardContent className="p-4">
              <p className="mb-3 font-heading text-sm font-semibold text-text-main">Breakdown by status</p>
              <div className="flex flex-wrap gap-4">
                {[1, 2, 3, 4, 5].map((code) => {
                  const meta = subscriptionStatusMeta(code);
                  const value = { 1: r.subscriptions_by_status.active, 2: r.subscriptions_by_status.pending, 3: r.subscriptions_by_status.deactivated, 4: r.subscriptions_by_status.expired, 5: r.subscriptions_by_status.deleted }[code];
                  return (
                    <div key={code} className="flex items-center gap-2" data-testid={`subs-report-status-${code}`}>
                      <StatusBadge label={meta.label} variant={meta.variant} />
                      <span className="font-heading text-sm font-semibold text-text-main">{formatNumber(value)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="subs-report-plan-table-card">
            <CardContent className="p-0">
              <p className="p-4 pb-0 font-heading text-sm font-semibold text-text-main">By plan</p>
              <Table data-testid="subs-report-plan-table">
                <TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Subscriptions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {r.subscriptions_by_plan.map((p) => (
                    <TableRow key={p.plan_id} data-testid={`subs-report-plan-row-${p.plan_id}`}>
                      <TableCell className="font-medium">{p.plan_name}</TableCell>
                      <TableCell>{formatNumber(p.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
